/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, useTheme } from '@mui/material';

import {
    ColumnDatatable,
    FiltersDatatableList,
    StoreDatatable,
} from '@europrocurement/flexy-datatable';
import {
    EmptyBox,
    FlexyIconButton,
    HeaderActionButton,
    HeaderListSkeleton,
    ListHeader,
    ListSkeleton,
    modalActionsType,
    ModalContext,
    Scrollbar,
    SkeletonLoader,
} from '@europrocurement/flexy-components';
import { editIcon, NamedIconsType } from '@europrocurement/l2d-icons';
import { ModelItem, useSociete } from '@europrocurement/l2d-domain';
import { useInitialLoadingState, useKeyboard } from '@europrocurement/l2d-hooks';
import { EuroprocApiResponseStatus, getDataThunkType } from '@europrocurement/l2d-redux-utils';

import PreviewCard from './PreviewCard';
import { AbstractFormType } from '../forms/types';
import ModalFormTitle from './form/ModalFormTitle';
import GenericSearchBar from '../../Societes/components/GenericSearchBar';
import { useSearchBar, SearchBarProvider } from '../../Societes/providers/SearchBarProvider';

type ListingWithSearchAndPreviewProps = {
    listingTitle: string;
    listingButton: {
        action: () => void;
        icon: NamedIconsType;
        label: string;
    };
    listingColumns: ColumnDatatable<unknown>[];
    filters?: FiltersDatatableList;
    form?: React.FunctionComponent<AbstractFormType>;
    model: ModelItem<unknown>;
    filtersControl: boolean;
    searchBarType?: string;
    hidePreview?: boolean;
};

type SelectableItem = { id: string | number };

const MODEL_NAME_MAP = {
    additional_field: 'champ complémentaire',
    action: 'action',
    article: 'article',
    document: 'document',
    offer_package: 'package',
    catalog: 'catalogue',
    offer: 'offre',
    offer_option: 'option',
    offer_group: 'groupement',
} as const;

const FEMININE_ARTICLE_MODELS = ['offer', 'offer_option'];

/**
 * Composant de listing avec SearchBar unifiée et panneau de prévisualisation latéral.
 * Utilisé dans le dashboard des offres pour afficher une liste d'entités
 * avec une carte de preview sur la droite et une recherche unifiée en haut.
 */
const ListingWithSearchAndPreview: React.FunctionComponent<ListingWithSearchAndPreviewProps> =
    function (props) {
        const {
            listingTitle,
            listingButton,
            listingColumns,
            filters,
            form: Form,
            model,
            filtersControl = false,
            searchBarType,
            hidePreview = false,
        } = props;

        const dispatch = useDispatch();
        const theme = useTheme();
        const xIdSociete = useSociete();
        const { modalActions }: { modalActions: modalActionsType } = useContext(ModalContext);
        const { setSearchBarType, setIsSearchBarPath } = useSearchBar();

        const [formId, setFormId] = useState<string | null>(null);

        // Configurer le type de SearchBar si spécifié
        useEffect(() => {
            if (searchBarType) {
                setSearchBarType(searchBarType as any);
                setIsSearchBarPath(true);

                // Effacer le search
                setTimeout(() => {
                    dispatch({
                        type: `${model.sliceName}/set${model.key}Search`,
                        payload: { search: '' },
                    });
                }, 0);
            }
            return () => {
                setSearchBarType(undefined);
                setIsSearchBarPath(false);
            };
        }, [
            dispatch,
            model.key,
            model.sliceName,
            searchBarType,
            setIsSearchBarPath,
            setSearchBarType,
        ]);

        // Effacer le search quand le searchBarType change
        useEffect(() => {
            if (searchBarType) {
                // Effacer le search dans le slice Redux correspondant
                const clearSearchAction = {
                    type: `${model.sliceName}/set${model.key.charAt(0).toUpperCase() + model.key.slice(1)}Search`,
                    payload: { search: '' },
                };
                dispatch(clearSearchAction);
            }
        }, [searchBarType, model.sliceName, model.key, dispatch]);

        const { datasourcesThunks, selector, sliceName } = model;
        const fetchAction = datasourcesThunks.main.getData as getDataThunkType<never>;

        const modelSelector = useSelector(selector);
        const datasource = modelSelector.main;
        const selectedEntity = datasource.selected;
        const selectedEntityResponseStatus: EuroprocApiResponseStatus = datasource.selectedStatus;

        const { isInitialLoading, isInError } = useInitialLoadingState(datasource);

        // Déclencher le fetch initial au mount
        useEffect(() => {
            dispatch(datasourcesThunks.main.getData({}) as any);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        const deleteSelectedItem = useCallback(() => {
            dispatch({ type: `${sliceName}/deletemainSelected` });
        }, [dispatch, sliceName]);

        const onSelectItem = useCallback(
            (item: SelectableItem) => {
                dispatch(datasourcesThunks.main.getSelected(item) as any);
            },
            [datasourcesThunks.main, dispatch],
        );

        const reloadList = useCallback(() => {
            dispatch(datasourcesThunks.main.getData({}) as any);
        }, [datasourcesThunks.main, dispatch]);

        const refreshSelectedEntity = useCallback(() => {
            if (selectedEntity) {
                onSelectItem(selectedEntity);
            }
        }, [onSelectItem, selectedEntity]);

        const afterUpdate = useCallback(() => {
            reloadList();
            refreshSelectedEntity();
        }, [reloadList, refreshSelectedEntity]);

        const getEditionTitle = useCallback(() => {
            const article = FEMININE_ARTICLE_MODELS.includes(model.key) ? 'une' : 'un';
            const modelName = MODEL_NAME_MAP[model.key as keyof typeof MODEL_NAME_MAP] ?? model.key;
            return `Édition d'${article} ${modelName}`;
        }, [model.key]);

        const onEdit = useCallback(() => {
            if (!selectedEntity || !Form) return;

            modalActions.call(
                <>
                    <ModalFormTitle
                        icon={model.icon}
                        title={getEditionTitle()}
                    />
                    <Form
                        entity={selectedEntity}
                        afterSubmit={afterUpdate}
                    />
                </>,
            );
        }, [Form, afterUpdate, getEditionTitle, modalActions, model.icon, selectedEntity]);

        useEffect(() => {
            const selectedId = selectedEntity?.id;
            if (selectedId === formId) {
                onEdit();
                setFormId(null);
            }
        }, [selectedEntity, formId, onEdit]);

        const columns = useMemo(
            () => [
                ...listingColumns,
                {
                    label: 'Actions',
                    displayLabel: true,
                    render: (item: { id: string }) =>
                        item.id && (
                            <FlexyIconButton
                                displayTooltip
                                icon={editIcon}
                                onClick={() => setFormId(item.id)}
                            />
                        ),
                    isDisplayed: true,
                },
            ],
            [listingColumns],
        );

        const keyboardActions = useMemo(
            () => [{ key: 'Escape', action: deleteSelectedItem }],
            [deleteSelectedItem],
        );
        useKeyboard(keyboardActions);

        const canSelect = useCallback(
            (item: SelectableItem): boolean => {
                const isAlreadySelected = selectedEntity ? selectedEntity.id === item.id : false;
                const selectionIsPending = selectedEntityResponseStatus === 'loading';
                return !isAlreadySelected && !selectionIsPending;
            },
            [selectedEntity, selectedEntityResponseStatus],
        );

        const onClickRow = useCallback(
            (_: React.MouseEvent<HTMLElement>, item: SelectableItem) => {
                if (canSelect(item)) {
                    onSelectItem(item);
                }
            },
            [canSelect, onSelectItem],
        );

        const errorContent = useMemo(
            () => (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '50vh',
                        width: '100%',
                    }}
                >
                    <EmptyBox
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'space-evenly',
                            height: '50%',
                            width: '60%',
                        }}
                        text="Une erreur s'est produite lors de la récupération des données."
                        subText="Veuillez recharger la page. Si le problème persiste, contactez votre administrateur."
                    />
                </Box>
            ),
            [],
        );

        return (
            <SearchBarProvider>
                <SkeletonLoader
                    isLoading={isInitialLoading}
                    skeleton={
                        <>
                            <HeaderListSkeleton />
                            {searchBarType && (
                                <Box
                                    sx={{
                                        p: 2,
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    <GenericSearchBar />
                                </Box>
                            )}
                            <ListSkeleton
                                showPreview={!hidePreview}
                                columns={5}
                                rows={8}
                            />
                        </>
                    }
                    isInError={isInError}
                    errorChildren={errorContent}
                >
                    <Box>
                        <ListHeader
                            breadcrumbPath={[listingTitle]}
                            actions={
                                <HeaderActionButton
                                    label={listingButton.label}
                                    icon={listingButton.icon}
                                    onClick={listingButton.action}
                                    actionType="modal"
                                />
                            }
                        />

                        {searchBarType && (
                            <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                <GenericSearchBar />
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            {datasource && (
                                <Box
                                    sx={{ width: hidePreview ? '100%' : '70%', maxHeight: '65vh' }}
                                >
                                    <Scrollbar>
                                        <StoreDatatable
                                            dataSource={datasource}
                                            columns={columns}
                                            fetchData={fetchAction}
                                            filters={filters ?? []}
                                            filtersControl={filtersControl}
                                            hideColumnOptions
                                            selectedId={
                                                selectedEntity?.id
                                                    ? Number(selectedEntity.id)
                                                    : undefined
                                            }
                                            onClickRow={onClickRow}
                                            observers={[xIdSociete]}
                                        />
                                    </Scrollbar>
                                </Box>
                            )}

                            {!hidePreview ? (
                                <Box sx={{ width: '30%', maxHeight: '65vh' }}>
                                    <Scrollbar>
                                        <PreviewCard
                                            isPreviewEmpty={!selectedEntity}
                                            isPreviewLoading={
                                                selectedEntityResponseStatus === 'loading'
                                            }
                                            selectedEntity={selectedEntity}
                                            onEdit={onEdit}
                                        />
                                    </Scrollbar>
                                </Box>
                            ) : null}
                        </Box>
                    </Box>
                </SkeletonLoader>
            </SearchBarProvider>
        );
    };

export default ListingWithSearchAndPreview;
